import {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {getDiagnosisSelectionVersion, splitDiagnosisPair} from '@/features/projects/diagnosisVersion'
import {OtherPathologiesFormSchema} from '@/features/projects/formSchemas'
import {Diagnosis} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'

interface ProjectOtherPathologiesFieldsProps {
    index: number
    disabledIcd9: string[]
    disabledIcd10: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectOtherPathologiesFields = ({
    index,
    disabledIcd9,
    disabledIcd10,
    showTrashButton,
    remove
}: ProjectOtherPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<OtherPathologiesFormSchema>()
    const {field} = useController({control, name: `otherPathologies.${index}`})

    const isIcd9Selectable = getDiagnosisSelectionVersion() == 'ICD9'

    // Same rule as the main pathologies: both selects edit the same code, and each shows its own
    // classification of it.
    const {icd9, icd10} = useMemo(() => splitDiagnosisPair(field.value.diagnosis), [field.value.diagnosis])

    const handleSelectDiagnosis = useCallback(
        (value: Diagnosis | null) => {
            field.onChange({diagnosis: value})
        },
        [field.onChange]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5} align="flex-end">
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd9_cm', {number: index + 1})}
                    placeholder={t('projects:pathology_number_placeholder')}
                    value={icd9}
                    onChange={handleSelectDiagnosis}
                    disabledOptions={disabledIcd9}
                    disabled={!isIcd9Selectable}
                    version="ICD9"
                />
            </div>
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd10_im', {number: index + 1})}
                    placeholder={t('projects:pathology_number_icd10_im_placeholder')}
                    value={icd10}
                    onChange={handleSelectDiagnosis}
                    disabledOptions={disabledIcd10}
                    version="ICD10"
                />
            </div>
            {showTrashButton && (
                <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                    <Trash01Icon />
                </Button>
            )}
        </Flexbox>
    )
}
